/**
 * Client-side UUIDv7.
 *
 * Mints the id for every invoice command this app sends. That id is the primary
 * key of `restaurant_invoice_commands` on the server, so it IS the idempotency
 * key: a double-tapped Send, or one retried after the phone dropped signal
 * mid-request, carries the same id and applies exactly once.
 *
 * v7 rather than v4 because the leading 48 bits are the timestamp, so ids sort
 * chronologically — which keeps the primary-key index appending rather than
 * scattering inserts across the whole B-tree.
 *
 * `crypto.getRandomValues` is available in every browser this app supports and
 * in the Node runtime Next uses; there is deliberately no Math.random fallback,
 * since a predictable command id is a worse failure than a thrown one.
 *
 * Written with plain number arithmetic rather than BigInt because this app
 * targets below ES2020, where BigInt literals are unavailable, and keeping one
 * implementation across the two SPAs is worth more than the syntax. That is
 * safe: the timestamp is 48 bits and a double represents integers exactly up to
 * 53, so no precision is lost.
 */
export function uuidv7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // Math.floor + modulo rather than `>>`, which coerces to a signed 32-bit int
  // and would silently discard the top 16 bits of the timestamp.
  let now = Date.now();
  for (let index = 5; index >= 0; index -= 1) {
    bytes[index] = now % 256;
    now = Math.floor(now / 256);
  }

  // Version 7, RFC 4122 variant.
  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}
